import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthBrokerService } from 'auth-broker-api';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

let cachedToken: string | null = null;
let refreshInProgress$ = new BehaviorSubject<boolean>(false);

export const CxAirTokenExpiryInterceptor: HttpInterceptorFn = (req, next) => {
  // only apply to CXAir APIs
  if (!req.url.includes('cxair')) {
    return next(req);
  }

  const authBrokerService = inject(AuthBrokerService);

  const addAuthHeader = (token: string) =>
    req.clone({
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
    });

  const handleRequest = (token: string) => next(addAuthHeader(token));

  // If we already have a token → use it
  if (cachedToken) {
    return handleRequest(cachedToken).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          console.warn('🔄 Token expired, refreshing...');
          return refreshToken(authBrokerService).pipe(
            switchMap((newToken) => handleRequest(newToken)),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  // No token yet → fetch first time
  return refreshToken(authBrokerService).pipe(
    switchMap((newToken) => handleRequest(newToken)),
  );
};

//  Refresh helper
function refreshToken(authBrokerService: AuthBrokerService) {
  if (!refreshInProgress$.value) {
    refreshInProgress$.next(true);
    return authBrokerService.getToken().response$.pipe(
      switchMap((res) => {
        cachedToken = res?.token ?? null;
        refreshInProgress$.next(false);
        console.log('New token fetched');
        return [cachedToken!];
      }),
      catchError((err) => {
        refreshInProgress$.next(false);
        cachedToken = null;
        return throwError(() => err);
      }),
    );
  }

  // If a refresh is already happening, wait until it finishes
  return refreshInProgress$.pipe(
    filter((inProgress) => !inProgress),
    take(1),
    switchMap(() => [cachedToken!]),
  );
}
